import { gql, useQuery } from '@apollo/client';
import { Anchor, Box, Paragraph } from '@zillow/constellation-10';
import { Spinner } from '@zillow/constellation';
import React, { useContext, useMemo } from 'react';
import { Folder, ReadRepliedStatus, RenterProgressStatus } from '../../../../types/GraphqlTypes';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';

type Props = {
  showError: boolean;
  hasAuthError?: boolean;
};

const FOLDER_TEXT_OPTIONS = {
  [Folder.Archived]: 'No messages in your archive to show.',
  [Folder.Spam]: 'No messages in your spam to show.',
  [Folder.ShowAll]: 'No messages in your inbox to show.',
  [Folder.Inbox]: 'No messages in your inbox to show.',
  [Folder.Favorite]: 'No starred messages',
};

const EmptyContainer = ({ showError, hasAuthError }: Props) => {
  const { renterProgressStatus, folder, readRepliedStatus, isByProperty } = useContext(InboxPageContext);
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { inboxPageisLoading, inboxPageShowEmptyContainer } = inboxPageVariables;
  const hasFilters =
    isByProperty ||
    readRepliedStatus !== ReadRepliedStatus.ShowAll ||
    renterProgressStatus !== RenterProgressStatus.ShowAll ||
    inboxPageShowEmptyContainer;
  const showMyPropertiesLink = !hasFilters && !inboxPageisLoading && folder === Folder.Inbox;
  const messageContent = hasAuthError
    ? 'This account does not have access to this conversation. If you have another account, check to make sure you are logged into the right one.'
    : 'There was an error, please try again.';
  const messageListText = useMemo(() => {
    switch (renterProgressStatus) {
      case RenterProgressStatus.Tenants:
        return 'Conversations with current tenants will be shown here.';
      case RenterProgressStatus.Tours:
        return 'Once you have a scheduled tour with a renter, be sure to add a note to help organize your leads.';
      case RenterProgressStatus.Applicants:
        return 'Invite promising renters to apply to your listing.';
      case RenterProgressStatus.Inquiries:
        return 'Inquiries from potential renters will be shown here.';
      default:
        return FOLDER_TEXT_OPTIONS[folder];
    }
  }, [renterProgressStatus, folder]);

  return (
    <Box
      css={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        paddingBottom: 'tighter',
        paddingInline: 'default',
        textAlign: 'center',
      }}
    >
      {showError ? (
        <Paragraph css={{ color: hasAuthError ? 'text.dark' : 'red.400' }}>{messageContent}</Paragraph>
      ) : (
        <>
          {inboxPageisLoading ? (
            <>
              <Spinner size="lg" />
              <Paragraph css={{ color: 'gray.400' }}>Loading conversations...</Paragraph>
            </>
          ) : (
            <>
              <Paragraph css={{ color: 'gray.400', marginBottom: 'default' }}>{messageListText}</Paragraph>

              {showMyPropertiesLink && <Anchor href="/rental-manager/properties">Go to My Properties</Anchor>}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default EmptyContainer;

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPageIsLoading @client
    inboxPageShowEmptyContainer @client
  }
`;
